import "./subway.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import withScrollableView from "../../components/scrollable/withScrollableView";
import SplitView from "../../components/splitView";
import { SUBWAY_VIEW_THREAD_SETTINGS_HEIGHT, SUBWAY_VIEW_THREAD_SPLIT_WIDTH } from "../../constants";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { immutableRecords, PropTypeShapes } from "../../types";
import SubwayHeaderView from "./SubwayHeaderView";
import SubwayNodeCanvasView from "./SubwayNodeCanvasView";
import SubwaySyncScroller from "./SubwaySyncScroller";
import SubwayThreadColumnView from "./SubwayThreadColumnView";

class SubwayCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.requestViewBoundsForThreadColumnView = this.requestViewBoundsForThreadColumnView.bind(this);
    this.requestViewBoundsForNodeCanvasView = this.requestViewBoundsForThreadColumnView.bind(this);
  }

  requestViewBoundsForZoom(_zoom) {
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: this.props.containerRect.width,
      right: this.props.containerRect.width,
      top: 0,
      height: this.props.data.rect.height,
      bottom: this.props.data.rect.height
    });
  }

  requestViewBoundsForThreadColumnView(_zoom) {
    const SCALED_THREAD_COLUMN_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_SPLIT_WIDTH, this.props.fontSize);
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: SCALED_THREAD_COLUMN_WIDTH,
      right: SCALED_THREAD_COLUMN_WIDTH,
      top: 0,
      height: this.props.data.rect.height,
      bottom: this.props.data.rect.height
    });
  }

  requestViewBoundsForNodeCanvasView(_zoom) {
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: this.props.data.rect.width,
      right: this.props.data.rect.width,
      top: 0,
      height: this.props.data.rect.height,
      bottom: this.props.data.rect.height
    });
  }

  renderVerticalSplitView() {
    const childComponents = [
      <SubwayThreadColumnView
        key="threadColumn"
        {...this.props}
        requestViewBoundsForZoom={this.requestViewBoundsForThreadColumnView}
      />,
      <SubwayNodeCanvasView
        key="canvasView"
        {...this.props}
        requestViewBoundsForZoom={this.requestViewBoundsForNodeCanvasView}
      />
    ];

    return <Fragment>{childComponents}</Fragment>;
  }

  renderSyncScroller = () => {
    return (
      <SubwaySyncScroller
        key="syncScroller"
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        viewIds={List([this.props.viewId])}
      >
        {this.renderVerticalSplitView()}
      </SubwaySyncScroller>
    );
  };

  renderSplitView() {
    const SCALED_VIEW_THREAD_SETTINGS_HEIGHT = scalePixelsToFontSize(
      SUBWAY_VIEW_THREAD_SETTINGS_HEIGHT,
      this.props.fontSize
    );

    const horizontalSplitProps = {
      vertical: false,
      hasShadow: false,
      constraints: List([
        immutableRecords.SplitConstraintsRecord({
          minSize: SCALED_VIEW_THREAD_SETTINGS_HEIGHT,
          maxSize: SCALED_VIEW_THREAD_SETTINGS_HEIGHT,
          preferredSize: SCALED_VIEW_THREAD_SETTINGS_HEIGHT
        }),
        immutableRecords.SplitConstraintsRecord({
          canResize: false,
          preferredSize: Math.max(1, this.props.data.rect.height)
        })
      ])
    };

    const childComponents = [<SubwayHeaderView key="subwayHeader" {...this.props} />, this.renderSyncScroller()];

    return (
      <SplitView key="subwayCanvasSettingsSplitView" {...horizontalSplitProps}>
        {childComponents}
      </SplitView>
    );
  }

  render() {
    return <div className="subwayCanvasView">{this.renderSplitView()}</div>;
  }
}

SubwayCanvasView.propTypes = {
  isActiveView: PropTypes.bool.isRequired,
  fontSize: PropTypes.number.isRequired,
  viewId: PropTypes.string.isRequired,
  data: PropTypeShapes.subwayData.isRequired,
  allowedThreads: PropTypeShapes.listOptionGroupList.isRequired,
  visibleItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onNodeDrop: PropTypes.func.isRequired,
  onThreadToggle: PropTypes.func.isRequired,
  onAddThread: PropTypes.func.isRequired,
  onAddNode: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onItemDoubleClick: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onChangeDisplayOption: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  subwayViewSettings: PropTypeShapes.subwayViewSettings.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  typesById: ImmutablePropTypes.map,
  defaultType: PropTypes.string.isRequired,

  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  displayItems: ImmutablePropTypes.list.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

const scrollableViewOptions = {
  scrollHorizontally: true,
  scrollVertically: false,
  showHorizontalScrollbar: true,
  showVerticalScrollbar: false
};

export default withScrollableView(withContainerContext(SubwayCanvasView), scrollableViewOptions);
